import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { formatRelative } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, PlatformIcon, Spinner, type SocialMark } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, Copy, ExternalLink, KeyRound, ShieldCheck } from "lucide-react";
import { useState, type ReactNode } from "react";

import { QueryError, errorText, fieldError } from "../views";

type Integrations = components["schemas"]["IntegrationsDto"];
type Integration = components["schemas"]["IntegrationDto"];
type Provider = components["schemas"]["ProviderDto"];

const KEY = ["admin", "integrations"];

interface ProviderInfo {
  name: string;
  marks: SocialMark[];
  what: string;
  idLabel: string;
  secretLabel: string;
  versionLabel?: string;
  versionHint?: string;
  /** Setup steps for the provider's console. */
  steps: { title: string; body: ReactNode; link?: { href: string; label: string } }[];
  notes: string[];
}

/** Step-by-step setup for each provider's developer console (checked 2026-11-04). */
const INFO: Record<Provider, ProviderInfo> = {
  youtube: {
    name: "YouTube",
    marks: ["youtube"],
    what: "Public channels and videos: subscribers, views, likes and comments. Clients connect by pasting their channel link; nobody signs in.",
    idLabel: "",
    secretLabel: "API key",
    steps: [
      { title: "Create or choose a Google Cloud project", body: "Use one project for BrandEx (it can also hold the Google Business Profile client below).", link: { href: "https://console.cloud.google.com/projectcreate", label: "Google Cloud console" } },
      { title: "Enable the YouTube Data API v3", body: "APIs & Services → Library → YouTube Data API v3 → Enable.", link: { href: "https://console.cloud.google.com/apis/library/youtube.googleapis.com", label: "YouTube Data API v3" } },
      { title: "Create an API key", body: "APIs & Services → Credentials → Create credentials → API key.", link: { href: "https://console.cloud.google.com/apis/credentials", label: "Credentials" } },
      { title: "Restrict the key", body: "Edit the key → API restrictions → Restrict key → YouTube Data API v3. Optionally restrict it to the API and worker servers' IP addresses." },
      { title: "Paste the key below, save, then Test", body: "The test reads a well-known public channel with the key." },
    ],
    notes: [
      "Default quota: 10,000 units a day. A sync costs about 1 unit for the channel plus 2 per 50 videos, so thousands of channels fit.",
      "Quota increases are requested in the Google Cloud console (YouTube API Services audit).",
    ],
  },
  meta: {
    name: "Facebook & Instagram",
    marks: ["facebook", "instagram"],
    what: "One Meta app for Facebook Pages and Instagram professional accounts: posts, reactions, comments, shares and followers. Clients sign in with Facebook.",
    idLabel: "App ID",
    secretLabel: "App secret",
    versionLabel: "Graph API version",
    versionHint: "Like v24.0. Meta retires each version about two years after release; update it before then.",
    steps: [
      { title: "Create a Meta app", body: "Meta for Developers → My Apps → Create app → choose the Business type, connected to BrandEx's Business portfolio.", link: { href: "https://developers.facebook.com/apps/", label: "Meta for Developers" } },
      { title: "Add Facebook Login for Business and Instagram", body: "Add products → Facebook Login for Business, and Instagram (API setup with Facebook login)." },
      { title: "Register the redirect URI", body: "Facebook Login for Business → Settings → Valid OAuth Redirect URIs: paste the callback URL shown above." },
      { title: "Register the deauthorize and data-deletion callbacks", body: "Facebook Login → Settings → Deauthorize callback URL, and App settings → Basic → Data deletion request URL: paste the two Meta URLs shown above. They remove a person's connected Pages and synced posts when they remove BrandEx." },
      { title: "Fill App settings → Basic", body: "Privacy policy URL, terms URL, app icon, category and contact email. Copy the App ID and App secret into the form below." },
      { title: "Request permissions in App Review", body: "pages_show_list, pages_read_engagement, read_insights, business_management, instagram_basic, instagram_manage_insights. Explain that BrandEx shows a practice's own posts on its website; record a screencast of connecting a Page." },
      { title: "Complete Business Verification", body: "Business settings → Security Center → Start verification (company documents). Required for Advanced Access to other people's Pages." },
    ],
    notes: [
      "Until App Review passes, only people with a role on the app (admins, developers, testers) can connect their Pages. Use that to test with BrandEx's own Page.",
      "Instagram accounts must be Professional (Creator or Business) and linked to a Facebook Page.",
      "Page tokens don't expire; they stop working if the person removes BrandEx or loses access, and the client then sees “Reconnect needed”.",
    ],
  },
  tiktok: {
    name: "TikTok",
    marks: ["tiktok"],
    what: "The clinic's TikTok profile and public videos: followers, views, likes, comments and shares. Clients sign in with TikTok.",
    idLabel: "Client key",
    secretLabel: "Client secret",
    steps: [
      { title: "Create a TikTok developer app", body: "TikTok for Developers → Manage apps → Connect an app (organisation: BrandEx).", link: { href: "https://developers.tiktok.com/apps/", label: "TikTok for Developers" } },
      { title: "Add Login Kit and the Display API", body: "Products → Login Kit (Web) and Display API. Scopes: user.info.basic, user.info.profile, user.info.stats, video.list." },
      { title: "Register the redirect URI", body: "Login Kit → Redirect URI (Web): paste the callback URL shown above." },
      { title: "Copy the Client key and Client secret", body: "App details → Credentials. Paste them below, save, then Test." },
      { title: "Submit for review", body: "Describe the use (show a practice's own videos on its website), add the privacy policy and terms URLs and a demo video." },
    ],
    notes: [
      "Before approval, only the app's sandbox / target users can sign in.",
      "Access tokens last 24 hours and are renewed automatically; the renewal token lasts a year, after which the client reconnects.",
      "Reach is only available for TikTok Business accounts; views and likes work for every account.",
    ],
  },
  linkedin: {
    name: "LinkedIn",
    marks: ["linkedin"],
    what: "Company Pages' posts with likes, comments and followers. LinkedIn does not give apps access to personal profiles' posts. Clients sign in with LinkedIn.",
    idLabel: "Client ID",
    secretLabel: "Client secret",
    versionLabel: "API version",
    versionHint: "LinkedIn-Version header, YYYYMM (like 202510). Each version is supported for about a year; move to a newer one before it is retired.",
    steps: [
      { title: "Create a LinkedIn app", body: "LinkedIn Developers → Create app, associated with BrandEx's own Company Page, then verify the app from that Page.", link: { href: "https://www.linkedin.com/developers/apps", label: "LinkedIn Developers" } },
      { title: "Request the Community Management API", body: "Products → Community Management API → Request access. LinkedIn reviews the use case; the Development tier allows testing first." },
      { title: "Register the redirect URL", body: "Auth → OAuth 2.0 settings → Authorized redirect URLs: paste the callback URL shown above." },
      { title: "Check the scopes", body: "After approval, the Auth tab lists r_organization_social and rw_organization_admin." },
      { title: "Copy the Client ID and Primary Client Secret", body: "Auth → Application credentials. Paste them below, save, then Test." },
    ],
    notes: [
      "Access tokens last 60 days and renew with the refresh token (about a year).",
      "Whoever connects must be an admin of the client's Company Page.",
    ],
  },
  google: {
    name: "Google Business Profile",
    marks: ["google"],
    what: "Each chamber's Google reviews: stars, text, the practice's reply, the average rating and total. Clients sign in with the Google account that manages the profile.",
    idLabel: "OAuth client ID",
    secretLabel: "OAuth client secret",
    steps: [
      { title: "Request access to the Business Profile APIs", body: "Submit Google's access request form with BrandEx's project number. The APIs' quota is 0 until Google approves it.", link: { href: "https://developers.google.com/my-business/content/prereqs", label: "Business Profile API prerequisites" } },
      { title: "Enable the three APIs", body: "In the same Google Cloud project: My Business Account Management API, My Business Business Information API and Google My Business API (reviews).", link: { href: "https://console.cloud.google.com/apis/library", label: "API Library" } },
      { title: "Configure the OAuth consent screen", body: "External user type, app name, support email, privacy policy and terms URLs; add the scope https://www.googleapis.com/auth/business.manage.", link: { href: "https://console.cloud.google.com/apis/credentials/consent", label: "OAuth consent screen" } },
      { title: "Create the OAuth client", body: "Credentials → Create credentials → OAuth client ID → Web application → Authorized redirect URIs: paste the callback URL shown above." },
      { title: "Copy the client ID and secret", body: "Paste them below, save, then Test (Google confirms the pair without anyone signing in)." },
      { title: "Publish and verify the app", body: "Consent screen → Publish app, then complete Google's verification for the business.manage scope." },
    ],
    notes: [
      "While the consent screen is in Testing, only listed test users can sign in and their access expires after 7 days. Publish before connecting clients.",
      "Access tokens last an hour and are renewed before every sync.",
      "Each chamber with its own profile is a separate location; the practice picks the one to show.",
    ],
  },
};

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-border bg-muted/50 px-3 py-2 text-xs">{value}</code>
        <Button
          size="icon"
          variant="outline"
          aria-label={`Copy ${label}`}
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
        </Button>
      </div>
    </div>
  );
}

function status(app: Integration): { tone: "success" | "warning" | "neutral" | "accent"; label: string } {
  if (!app.configured) return { tone: "neutral", label: "Not set up" };
  if (!app.enabled) return { tone: "warning", label: "Turned off" };
  if (app.last_test === null || app.last_test === undefined) return { tone: "accent", label: "Saved, not tested" };
  return app.last_test.ok ? { tone: "success", label: "Working" } : { tone: "warning", label: "Test failed" };
}

function ProviderCard({ app, data }: { app: Integration; data: Integrations }) {
  const { api, locale } = useBrandex();
  const qc = useQueryClient();
  const info = INFO[app.provider];
  const [clientId, setClientId] = useState(app.client_id ?? "");
  const [secret, setSecret] = useState("");
  const [version, setVersion] = useState(app.api_version ?? "");
  const [enabled, setEnabled] = useState(app.configured ? app.enabled : true);
  const [confirm, setConfirm] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const res = await api.http.PUT("/v1/admin/integrations/{provider}", {
        params: { path: { provider: app.provider } },
        body: {
          client_id: app.has_client_id ? clientId.trim() : "",
          enabled,
          ...(secret.trim() === "" ? {} : { secret: secret.trim() }),
          ...(info.versionLabel === undefined ? {} : { api_version: version.trim() }),
        },
      });
      return res.data;
    },
    onSuccess: (d) => {
      setSecret("");
      if (d !== undefined) qc.setQueryData(KEY, d);
    },
  });
  const test = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/integrations/{provider}/test", { params: { path: { provider: app.provider } } });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const remove = useMutation({
    mutationFn: async () => {
      await api.http.DELETE("/v1/admin/integrations/{provider}", { params: { path: { provider: app.provider } } });
    },
    onSuccess: () => {
      setConfirm(false);
      setSecret("");
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const s = status(app);
  const id = `integration-${app.provider}`;
  return (
    <Card id={id}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex -space-x-1">
              {info.marks.map((m) => (
                <PlatformIcon key={m} platform={m} className="h-7 w-7 rounded-md bg-white" />
              ))}
            </span>
            <div>
              <CardTitle>{info.name}</CardTitle>
              <CardDescription className="max-w-2xl">{info.what}</CardDescription>
            </div>
          </div>
          <Badge tone={s.tone} className="whitespace-nowrap">{s.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5">
        {app.uses_oauth ? (
          app.callback_url ? (
            <div className="grid gap-3">
              <CopyField label="Redirect (callback) URL to register" value={app.callback_url} />
              {app.provider === "meta" && data.meta_deauthorize_url && data.meta_data_deletion_url ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <CopyField label="Deauthorize callback URL" value={data.meta_deauthorize_url} />
                  <CopyField label="Data deletion request URL" value={data.meta_data_deletion_url} />
                </div>
              ) : null}
            </div>
          ) : (
            <Alert tone="info">Set the public API URL above first: the redirect URL to register with {info.name} is built on it.</Alert>
          )
        ) : null}

        <form
          noValidate
          className="grid gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {app.has_client_id ? (
            <Field label={info.idLabel} error={fieldError(save.error, "client_id")}>
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} />
            </Field>
          ) : null}
          <Field
            label={info.secretLabel}
            hint={app.secret_hint ? `Stored: ••••${app.secret_hint}. Leave empty to keep it.` : "Stored encrypted; it is never shown again."}
            error={fieldError(save.error, "secret")}
          >
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" spellCheck={false} placeholder={app.secret_hint ? `••••${app.secret_hint}` : ""} />
          </Field>
          {info.versionLabel !== undefined ? (
            <Field label={info.versionLabel} hint={info.versionHint} error={fieldError(save.error, "api_version")}>
              <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder={app.default_version ?? ""} spellCheck={false} />
            </Field>
          ) : null}
          <label className="flex items-start gap-3 text-sm md:col-span-2">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>
              <span className="font-medium">Enabled</span>
              <span className="block text-muted-foreground">Off keeps the keys but stops new connections and syncs for {info.name}.</span>
            </span>
          </label>
          {save.isError && fieldError(save.error, "client_id", "secret", "api_version") === undefined ? (
            <div className="md:col-span-2"><QueryError error={save.error} /></div>
          ) : null}
          {save.isSuccess ? <div className="md:col-span-2"><Alert tone="success">Saved. Run Test to check the keys with {info.name}.</Alert></div> : null}
          <div className="flex flex-wrap items-center gap-2 md:col-span-2">
            <Button type="submit" loading={save.isPending}>
              <KeyRound className="h-4 w-4" aria-hidden />
              Save
            </Button>
            {app.configured ? (
              <Button type="button" variant="outline" loading={test.isPending} onClick={() => test.mutate()}>
                <ShieldCheck className="h-4 w-4" aria-hidden />
                Test
              </Button>
            ) : null}
            {app.configured ? (
              confirm ? (
                <span className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-2 py-1 text-sm">
                  Remove the keys? Connected clients stop syncing.
                  <Button type="button" size="sm" variant="destructive" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
                </span>
              ) : (
                <Button type="button" variant="ghost" onClick={() => setConfirm(true)}>Remove keys</Button>
              )
            ) : null}
          </div>
        </form>

        {app.last_test ? (
          <Alert tone={app.last_test.ok ? "success" : "error"}>
            Last test {formatRelative(locale, app.last_test.at)}: {app.last_test.message}
          </Alert>
        ) : null}
        {test.isError ? <QueryError error={test.error} /> : null}

        <details className="rounded-[var(--radius-lg)] border border-border p-4" open={!app.configured}>
          <summary className="flex cursor-pointer items-center gap-2 font-medium">
            <BookOpen className="h-4 w-4 text-primary" aria-hidden />
            How to set up {info.name}
          </summary>
          <ol className="mt-4 grid gap-4">
            {info.steps.map((step, i) => (
              <li key={step.title} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{i + 1}</span>
                <div className="grid gap-1">
                  <p className="font-medium">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                  {step.link ? (
                    <a href={step.link.href} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline">
                      {step.link.label}
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
          <ul className="mt-4 grid list-disc gap-1 pl-5 text-sm text-muted-foreground">
            {info.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </details>
      </CardContent>
    </Card>
  );
}

function PublicUrlCard({ data }: { data: Integrations }) {
  const { api, apiBaseUrl } = useBrandex();
  const qc = useQueryClient();
  const [url, setUrl] = useState(data.public_api_url ?? "");
  const save = useMutation({
    mutationFn: async () => {
      const res = await api.http.PUT("/v1/admin/integrations-settings", { body: { public_api_url: url.trim() === "" ? null : url.trim() } });
      return res.data;
    },
    onSuccess: (d) => {
      if (d !== undefined) qc.setQueryData(KEY, d);
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Public API URL</CardTitle>
        <CardDescription>
          The API's public https address. Providers send people back to it after they sign in, so the redirect URLs below are built on it and must be registered in each provider's console. For local
          testing, http://localhost:8080 works with Google and Meta in development mode.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Public API URL" hint={`This panel talks to ${apiBaseUrl}.`} error={fieldError(save.error, "public_api_url")}>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.brandexbd.com" inputMode="url" spellCheck={false} />
          </Field>
          <Button type="submit" loading={save.isPending}>Save</Button>
        </form>
        {save.isSuccess ? <Alert tone="success" className="mt-3">Saved. Register the redirect URLs below with each provider.</Alert> : null}
        {save.isError && fieldError(save.error, "public_api_url") === undefined ? <p className="mt-3 text-sm text-destructive">{errorText(save.error)}</p> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Social integrations (ADR 0025): BrandEx's developer app for each provider,
 * entered here by super admins (never in environment files), with the steps
 * to create each app.
 */
export function SocialIntegrations() {
  const { api } = useBrandex();
  const q = useQuery<Integrations>({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/integrations");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  if (q.isPending) return <Spinner label="Loading" />;
  if (q.isError) return <QueryError error={q.error} />;
  return (
    <div className="grid gap-6">
      <Alert tone="info">
        Keys are stored encrypted and are write-only: after saving, only their last four characters are shown. Every change and test is recorded in the audit log.
      </Alert>
      <PublicUrlCard data={q.data} />
      {q.data.apps.map((app) => (
        <ProviderCard key={app.provider} app={app} data={q.data} />
      ))}
    </div>
  );
}
